import React, { useCallback, useEffect, useRef, useState } from "react";
import Icon from "../../UI/Icon";
import Modal from "../../UI/Modal";
import ProductImage from "../../UI/ProductImage";
import { Badge } from "../../UI/Primitives";

/**
 * Product gallery: native scroll-snap swipe, thumbnails, hover zoom (pointer
 * devices), fullscreen lightbox with click-to-zoom, and arrow-key navigation.
 */
function ProductGallery({ images, name, outOfStock }) {
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [zoomed, setZoomed] = useState(false);
  const trackRef = useRef(null);
  const count = images.length;

  const goTo = useCallback(
    (index, smooth = true) => {
      const next = (index + count) % count;
      setActive(next);
      const track = trackRef.current;
      if (track) {
        track.scrollTo({ left: next * track.clientWidth, behavior: smooth ? "smooth" : "auto" });
      }
    },
    [count]
  );

  // Keep the active index in sync with swipe gestures.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const index = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
        setActive((prev) => (prev === index ? prev : index));
      });
    };
    track.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      track.removeEventListener("scroll", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  useEffect(() => {
    if (!lightbox) {
      setZoomed(false);
      return undefined;
    }
    const onKey = (event) => {
      if (event.key === "ArrowRight") goTo(active + 1, false);
      else if (event.key === "ArrowLeft") goTo(active - 1, false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [lightbox, active, goTo]);

  const onKeyDown = (event) => {
    if (event.key === "ArrowRight") {
      event.preventDefault();
      goTo(active + 1);
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      goTo(active - 1);
    }
  };

  const onZoomMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    event.currentTarget.style.setProperty("--zx", `${((event.clientX - rect.left) / rect.width) * 100}%`);
    event.currentTarget.style.setProperty("--zy", `${((event.clientY - rect.top) / rect.height) * 100}%`);
  };

  if (!count) return null;

  return (
    <div className="v-gallery">
      <div className="v-gallery__stage">
        <div
          ref={trackRef}
          className="v-gallery__track"
          tabIndex={0}
          role="region"
          aria-roledescription="carousel"
          aria-label={`${name} photos`}
          onKeyDown={onKeyDown}
        >
          {images.map((src, index) => (
            <div
              key={src}
              className="v-gallery__slide"
              role="group"
              aria-roledescription="slide"
              aria-label={`${index + 1} of ${count}`}
              onMouseMove={onZoomMove}
            >
              <button type="button" className="v-gallery__zoom" onClick={() => setLightbox(true)} aria-label="Open fullscreen gallery">
                <ProductImage
                  src={src}
                  alt={index === 0 ? name : `${name}, view ${index + 1}`}
                  ratio="1 / 1"
                  fit="contain"
                  priority={index === 0}
                  sizes="(min-width: 1024px) 50vw, 100vw"
                />
              </button>
            </div>
          ))}
        </div>

        {outOfStock ? (
          <div className="v-gallery__flag">
            <Badge tone="solid">Out of stock</Badge>
          </div>
        ) : null}

        <button type="button" className="v-gallery__fs v-icon-btn" onClick={() => setLightbox(true)} aria-label="View fullscreen">
          <Icon name="expand" size={18} />
        </button>

        {count > 1 ? (
          <>
            <button type="button" className="v-gallery__nav v-gallery__nav--prev" onClick={() => goTo(active - 1)} aria-label="Previous photo">
              <Icon name="chevronLeft" />
            </button>
            <button type="button" className="v-gallery__nav v-gallery__nav--next" onClick={() => goTo(active + 1)} aria-label="Next photo">
              <Icon name="chevronRight" />
            </button>
            <div className="v-gallery__dots" aria-hidden="true">
              {images.map((src, index) => (
                <span key={src} className={index === active ? "is-active" : ""} />
              ))}
            </div>
          </>
        ) : null}
      </div>

      {count > 1 ? (
        <div className="v-gallery__thumbs" role="tablist" aria-label="Choose photo">
          {images.map((src, index) => (
            <button
              key={src}
              type="button"
              role="tab"
              aria-selected={index === active}
              aria-label={`Photo ${index + 1}`}
              className={`v-gallery__thumb ${index === active ? "is-active" : ""}`}
              onClick={() => goTo(index)}
            >
              <img src={src} alt="" width="72" height="72" loading="lazy" decoding="async" />
            </button>
          ))}
        </div>
      ) : null}

      <Modal open={lightbox} onClose={() => setLightbox(false)} title={`${name} photos`} hideTitle closeButton variant="lightbox">
        <div className="v-lightbox">
          <div
            className={`v-lightbox__stage ${zoomed ? "is-zoomed" : ""}`}
            onMouseMove={onZoomMove}
            onClick={() => setZoomed((z) => !z)}
            role="presentation"
          >
            <img src={images[active]} alt={`${name}, view ${active + 1} of ${count}`} decoding="async" />
          </div>
          <p className="v-lightbox__hint">{zoomed ? "Click to zoom out" : "Click image to zoom"}</p>
          {count > 1 ? (
            <div className="v-lightbox__controls">
              <button type="button" className="v-icon-btn" onClick={() => goTo(active - 1, false)} aria-label="Previous photo">
                <Icon name="chevronLeft" />
              </button>
              <span className="v-lightbox__count" aria-live="polite">
                {active + 1} / {count}
              </span>
              <button type="button" className="v-icon-btn" onClick={() => goTo(active + 1, false)} aria-label="Next photo">
                <Icon name="chevronRight" />
              </button>
            </div>
          ) : null}
        </div>
      </Modal>
    </div>
  );
}

export default ProductGallery;
